import { useState, useCallback } from "react"
import { Link, router } from "@inertiajs/react"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowRight, Building2, Check, Copy, Mail, Trash2, WandSparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { DeleteAgentDialog } from "./DeleteAgentDialog"

function CopyableEmailAddress({ email }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [email])

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group/copy flex w-full items-center justify-between gap-4 rounded-lg bg-muted/50 hover:bg-muted px-3 py-2.5 transition-colors cursor-pointer"
    >
      <span className="min-w-0 truncate font-mono text-sm">{email}</span>
      <span className="relative text-[13px] text-slate-500 transition-colors group-hover/copy:text-foreground">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={copied ? "copied" : "copy"}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1.5"
          >
            {copied ? (
              <>
                <Check className="size-3.5 text-green-500" />
                <span className="text-green-600">Copied</span>
              </>
            ) : (
              <>
                <Copy className="size-3.5" />
                <span>Copy</span>
              </>
            )}
          </motion.span>
        </AnimatePresence>
      </span>
    </button>
  )
}

export function Settings({ agent, workspace_slug, workspaces = [] }) {
  const [deleteOpen, setDeleteOpen] = useState(false)
  const currentWorkspace = workspaces.find((ws) => ws.id === agent.workspace_id)

  function handleLearningToggle(checked) {
    router.patch(`/${workspace_slug}/agents/${agent.id}`, {
      agent: { learning: checked },
      redirect_to: "settings"
    })
  }

  return (
    <>

      {agent.direct_run_email_address && (
        <>
          <section className="flex flex-col max-w-lg">
            <div className="flex items-center gap-1.5">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-md font-medium">Direct email</h2>
            </div>
            <span className="text-sm text-muted-foreground leading-snug mt-0.5 mb-4">
              Email this agent directly and bypass trigger matching
            </span>
            <CopyableEmailAddress email={agent.direct_run_email_address} />
          </section>
          <Separator />
        </>
      )}

      <section className="flex flex-col max-w-lg">
        <div className="flex items-center gap-1.5">
          <WandSparkles className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-md font-medium">AI learning mode</h2>
        </div>
        <span className="text-sm text-muted-foreground leading-snug mt-0.5 mb-4">
          Automatically update the trigger, data, and requests fields as new emails arrive
        </span>

        <div className="rounded-lg overflow-hidden divide-y">
          <label
            htmlFor="learning"
            className={`flex items-center justify-between gap-6 px-3 py-2.5 cursor-pointer transition-colors ${
              agent.learning
                ? "bg-green-100 hover:bg-green-200 dark:bg-green-950/30 dark:hover:bg-green-950/50"
                : "bg-muted/50 hover:bg-muted"
            }`}
          >
            <div className="min-w-0">
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={agent.learning ? "on" : "off"}
                  initial={{ opacity: 0, y: 2 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -2 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-3 text-sm leading-snug"
                >
                  <span className={`font-medium ${agent.learning ? "text-green-600 dark:text-green-400" : ""}`}>
                    {agent.learning ? "On" : "Off"}
                  </span>
                  <span className="text-slate-500">
                    {agent.learning
                      ? "AI rewrites and updates trigger, data, and request fields"
                      : "Your edits to trigger, data, and request fields are preserved"}
                  </span>
                </motion.p>
              </AnimatePresence>
            </div>
            <Switch
              id="learning"
              checked={agent.learning}
              onCheckedChange={handleLearningToggle}
              className="shrink-0 cursor-pointer data-[state=checked]:bg-green-500"
            />
          </label>
        </div>
      </section>
      
      <Separator />

      {currentWorkspace && (
        <>
          <section className="flex flex-col max-w-lg">
            <div className="flex items-center gap-1.5">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-md font-medium">Workspace</h2>
            </div>
            <span className="text-sm text-muted-foreground leading-snug mt-0.5 mb-4">
              To move this agent to a different workspace, visit the workspace settings
            </span>
            <Button variant="outline" size="sm" asChild className="self-start group/ws">
              <Link href={`/workspaces/${currentWorkspace.slug}`}>
                {currentWorkspace.name}
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover/ws:text-foreground transition-colors" />
              </Link>
            </Button>
          </section>
          <Separator />
        </>
      )}

      <section className="flex flex-col max-w-lg">
        <div className="text-destructive flex items-center gap-1.5">
          <Trash2 className="h-4 w-4" />
          <h2 className="text-md font-medium">Delete</h2>
        </div>
        <span className="text-sm text-muted-foreground leading-snug mt-0.5 mb-4">
          Once deleted, all run and preview data will be permanently removed
        </span>
        <Button
          variant="destructive"
          size="sm"
          className="self-start"
          onClick={() => setDeleteOpen(true)}
        >
          Delete agent
        </Button>
        <DeleteAgentDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          agentId={agent.id}
          workspaceSlug={workspace_slug}
        />
      </section>

    </>
  )
}
